// Step 5: pairing the ops tablet. The tablet app shows a code; the ops centre reads it
// to us and we type it here. The set-up is then approved on the tablet itself, and from
// then on it sends the alerts and approves every change.

import { useState, type FormEvent } from 'react';
import type { SiteDraft } from '../../../../src/draft.ts';
import { api, ApiError, message } from '../../api.ts';
import { ago, tabletOnline } from '../../format.ts';
import type { SiteView } from '../../types.ts';

interface Found {
  code: string;
  model: string;
  osVersion: string;
  appVersion: string;
  createdAt: string;
}

const tidy = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
const shown = (s: string) => (s.length > 4 ? `${s.slice(0, 4)}-${s.slice(4)}` : s);

export function TabletStep({ site, reload }: { site: SiteView; reload: () => void }) {
  const draft = site.draft as SiteDraft;
  // The code box shows by itself when there's no tablet yet; after that it's "Add a spare tablet".
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [found, setFound] = useState<Found | null>(null);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const find = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFound(null);
    setBusy(true);
    try {
      const f = await api<Found>('GET', `/pairings/${encodeURIComponent(tidy(code))}`);
      setFound(f);
      setLabel(draft.tablets.length ? 'Spare ops tablet' : 'Ops centre tablet');
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? "That code wasn't found. Codes last 30 minutes and work once. Check it with the ops centre." : message(err));
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!found) return;
    setError(null);
    setBusy(true);
    try {
      await api('POST', `/sites/${site.siteId}/tablets`, { code: found.code, label });
      setFound(null);
      setCode('');
      setAdding(false);
      reload();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (keyId: string, name: string) => {
    if (!confirm(`Take ${name} off this site? ${site.live ? 'It stops sending once the change is approved on the ops tablet.' : ''}`)) return;
    setError(null);
    try {
      await api('DELETE', `/sites/${site.siteId}/tablets/${keyId}`);
      reload();
    } catch (err) {
      setError(message(err));
    }
  };

  const live = new Set(site.bundle?.tablets.map((t) => t.keyId) ?? []);
  return (
    <>
      <ol className="stack muted" style={{ gap: 6, margin: 0, paddingLeft: 22, maxWidth: 760 }}>
        <li>Install Z.A.S. Ops on the tablet, from the App Store or Google Play.</li>
        <li>Open it. It shows a code.</li>
        <li>Type the code here.</li>
      </ol>

      {draft.tablets.length > 0 && (
        <div className="table" style={{ maxWidth: 760 }}>
          {draft.tablets.map((t) => {
            const device = site.tablets.find((d) => d.keyId === t.keyId);
            const canSend = live.has(t.keyId);
            return (
              <div key={t.keyId} className="tr" style={{ gridTemplateColumns: 'minmax(0, 1fr) auto auto', alignItems: 'center' }}>
                <span className="stack" style={{ gap: 2 }}>
                  <span>{t.label}</span>
                  <span className="muted small">{device ? [device.model, device.osVersion, device.appVersion && `Z.A.S. Ops ${device.appVersion}`].filter(Boolean).join(' · ') : 'Tablet'}</span>
                </span>
                <span className="stack small" style={{ gap: 2, textAlign: 'right' }}>
                  <span className={canSend ? 'green' : 'amber'}>{canSend ? 'Can send alerts' : 'Can send once the set-up is approved'}</span>
                  {device?.lastSeenAt && (
                    <span className={tabletOnline(device.lastSeenAt) ? 'green' : 'faint'}>
                      {tabletOnline(device.lastSeenAt) ? 'Online' : 'Last seen'} · {ago(device.lastSeenAt)}
                    </span>
                  )}
                </span>
                <button className="linkish" onClick={() => remove(t.keyId, t.label)} aria-label={`Remove ${t.label}`}>
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      {adding || draft.tablets.length === 0 ? (
        <form className="card" style={{ gap: 14, maxWidth: 520 }} onSubmit={find}>
          <label className="field">
            Code on the tablet
            <input
              className="input mono"
              style={{ fontSize: 22, letterSpacing: '0.12em', maxWidth: 240 }}
              value={shown(tidy(code))}
              onChange={(e) => {
                setCode(tidy(e.target.value).slice(0, 8));
                setFound(null);
              }}
              placeholder="ABCD-2345"
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </label>
          {!found && (
            <div className="row">
              <button className="btn primary small" type="submit" disabled={busy || tidy(code).length !== 8}>
                Find the tablet
              </button>
              {draft.tablets.length > 0 && (
                <button className="btn small" type="button" onClick={() => setAdding(false)}>
                  Cancel
                </button>
              )}
            </div>
          )}
          {found && (
            <div className="stack" style={{ gap: 12 }}>
              <span className="green small" style={{ fontWeight: 500 }}>
                Tablet found
              </span>
              <span>
                {found.model} · {found.osVersion} · Z.A.S. Ops {found.appVersion}
              </span>
              <label className="field">
                Name for this tablet
                <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} required />
              </label>
              <div className="row">
                <button className="btn primary small" type="button" onClick={add} disabled={busy || !label.trim()}>
                  Add this tablet
                </button>
                <button className="btn small" type="button" onClick={() => setFound(null)}>
                  Not this one
                </button>
              </div>
            </div>
          )}
          {error && <p className="error">{error}</p>}
        </form>
      ) : (
        <div className="row">
          <button className="btn" onClick={() => setAdding(true)}>
            Add a spare tablet
          </button>
          {error && <span className="error">{error}</span>}
        </div>
      )}

    </>
  );
}
